import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowUpRight, ArrowDownLeft, Package, Clock, Filter, Minus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const movementTypeLabels = {
  purchase_order: { label: 'Purchase Order', color: 'text-green-400', icon: ArrowDownLeft },
  sale: { label: 'Sale', color: 'text-red-400', icon: ArrowUpRight },
  adjustment: { label: 'Adjustment', color: 'text-blue-400', icon: Package },
  internal_use: { label: 'Internal Use', color: 'text-orange-400', icon: Minus },
  return: { label: 'Return', color: 'text-purple-400', icon: ArrowDownLeft },
  damaged: { label: 'Damaged', color: 'text-red-400', icon: Package },
};

export default function AdminStockMovements() {
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChargeOutModal, setShowChargeOutModal] = useState(false);
  const [filter, setFilter] = useState({ product_id: 'all', movement_type: 'all' });
  const [chargeOutForm, setChargeOutForm] = useState({
    product_id: '',
    quantity: 1,
    reason: '',
    notes: '',
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      let url = `${API}/admin/stock/movements?limit=200`;
      if (filter.product_id && filter.product_id !== 'all') url += `&product_id=${filter.product_id}`;
      if (filter.movement_type && filter.movement_type !== 'all') url += `&movement_type=${filter.movement_type}`;
      
      const [movementsRes, productsRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products?limit=50`),
      ]);
      setMovements(movementsRes.data);
      setProducts(productsRes.data.products || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleChargeOut = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/stock/charge-out`, chargeOutForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock charged out successfully');
      setShowChargeOutModal(false);
      setChargeOutForm({ product_id: '', quantity: 1, reason: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to charge out stock');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-stock-movements">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-black text-white uppercase tracking-tight">Stock History</h1>
            <p className="text-zinc-400 mt-1">Track all stock movements and internal charge-outs</p>
          </div>
          <Button 
            onClick={() => setShowChargeOutModal(true)}
            className="bg-orange-500 text-white hover:bg-orange-600"
            data-testid="charge-out-btn"
          >
            <Minus className="h-4 w-4 mr-2" /> Charge Out Stock
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-end">
          <div className="w-64">
            <Label className="text-xs text-zinc-500">Filter by Product</Label>
            <Select value={filter.product_id} onValueChange={(v) => setFilter({ ...filter, product_id: v })}>
              <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                <SelectValue placeholder="All Products" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {products.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.sku} - {p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Label className="text-xs text-zinc-500">Filter by Type</Label>
            <Select value={filter.movement_type} onValueChange={(v) => setFilter({ ...filter, movement_type: v })}>
              <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="purchase_order">Purchase Orders</SelectItem>
                <SelectItem value="sale">Sales</SelectItem>
                <SelectItem value="adjustment">Adjustments</SelectItem>
                <SelectItem value="internal_use">Internal Use</SelectItem>
                <SelectItem value="return">Returns</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(filter.product_id !== 'all' || filter.movement_type !== 'all') && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setFilter({ product_id: 'all', movement_type: 'all' })}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Movement List */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-800">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-zinc-400 uppercase">Date</th>
                <th className="text-left p-3 text-xs font-medium text-zinc-400 uppercase">Type</th>
                <th className="text-left p-3 text-xs font-medium text-zinc-400 uppercase">Product</th>
                <th className="text-right p-3 text-xs font-medium text-zinc-400 uppercase">Qty</th>
                <th className="text-left p-3 text-xs font-medium text-zinc-400 uppercase">Reference</th>
                <th className="text-left p-3 text-xs font-medium text-zinc-400 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="p-4 text-center text-zinc-400">Loading...</td>
                </tr>
              ) : movements.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-zinc-400">No stock movements recorded yet.</td>
                </tr>
              ) : (
                movements.map(movement => {
                  const typeInfo = movementTypeLabels[movement.movement_type] || { label: movement.movement_type, color: 'text-zinc-400', icon: Package };
                  const Icon = typeInfo.icon;
                  return (
                    <tr key={movement.id} className="border-t border-zinc-800 hover:bg-zinc-800/50">
                      <td className="p-3 text-sm text-zinc-400">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          {new Date(movement.created_at).toLocaleString()}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className={`flex items-center gap-2 text-sm ${typeInfo.color}`}>
                          <Icon className="h-4 w-4" />
                          {typeInfo.label}
                        </div>
                      </td>
                      <td className="p-3">
                        <p className="text-sm text-white">{movement.product_name}</p>
                        <p className="text-xs text-zinc-500">{movement.sku}</p>
                      </td>
                      <td className={`p-3 text-right text-sm font-bold ${movement.quantity > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                      </td>
                      <td className="p-3 text-sm text-zinc-400">
                        {movement.reference_type && (
                          <span className="text-xs px-2 py-0.5 bg-zinc-800 rounded">
                            {movement.reference_type.toUpperCase()}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-zinc-500 max-w-xs truncate">
                        {movement.reason || movement.notes || '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Charge Out Modal */}
        <Dialog open={showChargeOutModal} onOpenChange={setShowChargeOutModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Charge Out Stock (Internal Use)</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleChargeOut} className="space-y-4">
              <div>
                <Label>Product *</Label>
                <Select 
                  value={chargeOutForm.product_id} 
                  onValueChange={(v) => setChargeOutForm({ ...chargeOutForm, product_id: v })}
                >
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.sku} - {p.name} (Stock: {p.stock_qty})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  min="1"
                  value={chargeOutForm.quantity}
                  onChange={(e) => setChargeOutForm({ ...chargeOutForm, quantity: parseInt(e.target.value) || 0 })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  required
                />
              </div>
              <div>
                <Label>Reason *</Label>
                <Input
                  value={chargeOutForm.reason}
                  onChange={(e) => setChargeOutForm({ ...chargeOutForm, reason: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="e.g., Demo unit, Office use, Testing"
                  required
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={chargeOutForm.notes}
                  onChange={(e) => setChargeOutForm({ ...chargeOutForm, notes: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  rows={2}
                  placeholder="Additional details (optional)"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowChargeOutModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-orange-500 text-white hover:bg-orange-600">
                  <Minus className="h-4 w-4 mr-2" /> Charge Out
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
